import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { RouteErrorBoundary } from 'components/errors/error-boundary';

import { ProtectedRoute } from 'lib/auth';

import { AppRoot } from './routes/app/root';

const createAppRouter = (_queryClient: QueryClient) =>
    createBrowserRouter([
        {
            path: '/',
            errorElement: <RouteErrorBoundary />,
            lazy: async () => {
                const { LandingRoute } = await import('./routes/landing');
                return { Component: LandingRoute };
            },
        },
        {
            path: '/authenticate',
            errorElement: <RouteErrorBoundary />,
            lazy: async () => {
                const { AuthenticateRoute } = await import(
                    './routes/authenticate'
                );
                return { Component: AuthenticateRoute };
            },
        },
        {
            path: '/terms-of-service',
            lazy: async () => {
                const { TermsOfServiceRoute } = await import(
                    './routes/terms-of-service'
                );
                return { Component: TermsOfServiceRoute };
            },
        },
        {
            path: '/privacy-policy',
            lazy: async () => {
                const { PrivacyPolicyRoute } = await import(
                    './routes/privacy-policy'
                );
                return { Component: PrivacyPolicyRoute };
            },
        },
        {
            path: '/acceptable-use-policy',
            lazy: async () => {
                const { AcceptableUsePolicyRoute } = await import(
                    './routes/acceptable-use-policy'
                );
                return { Component: AcceptableUsePolicyRoute };
            },
        },

        {
            path: '/app',
            errorElement: <RouteErrorBoundary />,
            element: (
                <ProtectedRoute>
                    <AppRoot />
                </ProtectedRoute>
            ),
            children: [
                {
                    path: '',
                    lazy: async () => {
                        const { HomeRoute } = await import('./routes/app/home');
                        return { Component: HomeRoute };
                    },
                },

                {
                    path: 'medicard-claims',
                    lazy: async () => {
                        const { MedicardClaimsRoute } = await import(
                            './routes/app/medicard/claims'
                        );
                        return { Component: MedicardClaimsRoute };
                    },
                },
                {
                    path: 'insurance-analytics',
                    lazy: async () => {
                        const { MedicardAnalyticsDashboardsRoute } =
                            await import(
                                './routes/app/medicard/analytics-dashboards'
                            );
                        return { Component: MedicardAnalyticsDashboardsRoute };
                    },
                },
                {
                    path: 'maxicare-analytics-chat',
                    lazy: async () => {
                        const { MedicardAnalyticsRoute } = await import(
                            './routes/app/medicard/analytics'
                        );
                        return { Component: MedicardAnalyticsRoute };
                    },
                },
                {
                    path: 'medication-analytics',
                    lazy: async () => {
                        const { MedicardMedicationAnalyticsRoute } =
                            await import(
                                './routes/app/medicard/medication-analytics'
                            );
                        return { Component: MedicardMedicationAnalyticsRoute };
                    },
                },
                {
                    path: 'medicard-poc-grading',
                    lazy: async () => {
                        const { MedicardPocGradingRoute } = await import(
                            './routes/app/medicard/poc-grading'
                        );
                        return { Component: MedicardPocGradingRoute };
                    },
                },
                {
                    path: 'medical-code-lookup',
                    lazy: async () => {
                        const { MedicalCodeLookupRoute } = await import(
                            './routes/app/medical-code-lookup'
                        );
                        return { Component: MedicalCodeLookupRoute };
                    },
                },
                {
                    path: 'ocr-labeling',
                    lazy: async () => {
                        const { OcrLabelingRoute } = await import(
                            './routes/app/ocr-labeling'
                        );
                        return { Component: OcrLabelingRoute };
                    },
                },
                {
                    path: 'ocr-labeling/:workflow',
                    lazy: async () => {
                        const { OcrLabelingRoute } = await import(
                            './routes/app/ocr-labeling'
                        );
                        return { Component: OcrLabelingRoute };
                    },
                },
                {
                    path: 'ocr-labeling/:workflow/:llmInvocationId',
                    lazy: async () => {
                        const { OcrLabelingRoute } = await import(
                            './routes/app/ocr-labeling'
                        );
                        return { Component: OcrLabelingRoute };
                    },
                },
                {
                    path: 'philhealth-claim-automation',
                    lazy: async () => {
                        const { PhilHealthClaimAutomationRoute } = await import(
                            './routes/app/philhealth-claim-automation'
                        );
                        return { Component: PhilHealthClaimAutomationRoute };
                    },
                },
                {
                    path: 'maxicare-cpt-mapping',
                    lazy: async () => {
                        const { MaxicareCptMappingRoute } = await import(
                            './routes/app/maxicare-cpt-mapping'
                        );
                        return { Component: MaxicareCptMappingRoute };
                    },
                },
                {
                    path: 'transcript-demo',
                    lazy: async () => {
                        const { TranscriptDemoRoute } = await import(
                            './routes/app/transcript-demo'
                        );
                        return { Component: TranscriptDemoRoute };
                    },
                },
                {
                    path: 'aster-analytics-demo',
                    lazy: async () => {
                        const { AsterAnalyticsDemoRoute } = await import(
                            './routes/app/aster-analytics-demo'
                        );
                        return { Component: AsterAnalyticsDemoRoute };
                    },
                },
                {
                    path: 'analytics-ai',
                    lazy: async () => {
                        const { AnalyticsDemoRoute } = await import(
                            './routes/app/analytics-demo'
                        );
                        return { Component: AnalyticsDemoRoute };
                    },
                },
                {
                    path: 'aster-invoice-extraction',
                    lazy: async () => {
                        const { AsterInvoiceExtractionRoute } = await import(
                            './routes/app/aster-invoice-extraction'
                        );
                        return { Component: AsterInvoiceExtractionRoute };
                    },
                },
                {
                    path: 'aster-invoice-extraction/:documentId',
                    lazy: async () => {
                        const { AsterInvoiceExtractionDetailRoute } =
                            await import(
                                './routes/app/aster-invoice-extraction-detail'
                            );
                        return { Component: AsterInvoiceExtractionDetailRoute };
                    },
                },
                {
                    path: 'projects',
                    lazy: async () => {
                        const { ProjectsRoute } = await import(
                            './routes/app/projects/projects'
                        );
                        return { Component: ProjectsRoute };
                    },
                },
                {
                    path: 'projects/create',
                    lazy: async () => {
                        const { CreateProjectRoute } = await import(
                            './routes/app/projects/create'
                        );
                        return { Component: CreateProjectRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId',
                    lazy: async () => {
                        const { ProjectRoute } = await import(
                            './routes/app/projects/project'
                        );
                        return { Component: ProjectRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId/data-quality-review',
                    lazy: async () => {
                        const { DataQualityReviewRoute } = await import(
                            './routes/app/projects/data-quality-review'
                        );
                        return { Component: DataQualityReviewRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId/data-quality-review/:runId',
                    lazy: async () => {
                        const { DataQualityReviewRunRoute } = await import(
                            './routes/app/projects/data-quality-review-run-details'
                        );
                        return { Component: DataQualityReviewRunRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId/configuration',
                    lazy: async () => {
                        const { ProjectConfigurationRoute } = await import(
                            './routes/app/projects/configuration'
                        );
                        return { Component: ProjectConfigurationRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId/implementations',
                    lazy: async () => {
                        const { ProjectImplementationsRoute } = await import(
                            './routes/app/projects/implementations'
                        );
                        return { Component: ProjectImplementationsRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId/implementations/:implementationId',
                    lazy: async () => {
                        const { ProjectImplementationRoute } = await import(
                            './routes/app/projects/implementation'
                        );
                        return { Component: ProjectImplementationRoute };
                    },
                },
                {
                    path: 'projects/:projectGroupId/implementations/:implementationId/evaluations',
                    lazy: async () => {
                        const { ProjectImplementationEvaluationsRoute } =
                            await import(
                                './routes/app/projects/implementation-evaluations'
                            );
                        return {
                            Component: ProjectImplementationEvaluationsRoute,
                        };
                    },
                },
                {
                    path: 'datasets',
                    lazy: async () => {
                        const { DatasetsRoute } = await import(
                            './routes/app/datasets/datasets'
                        );
                        return { Component: DatasetsRoute };
                    },
                },
                {
                    path: 'datasets/create',
                    lazy: async () => {
                        const { CreateDatasetRoute } = await import(
                            './routes/app/datasets/create'
                        );
                        return { Component: CreateDatasetRoute };
                    },
                },
                {
                    path: 'datasets/:datasetGroupId',
                    lazy: async () => {
                        const { DatasetRoute } = await import(
                            './routes/app/datasets/dataset'
                        );
                        return { Component: DatasetRoute };
                    },
                },
                {
                    path: 'datasets/:datasetGroupId/edit',
                    lazy: async () => {
                        const { EditDatasetRoute } = await import(
                            './routes/app/datasets/edit'
                        );
                        return { Component: EditDatasetRoute };
                    },
                },
                {
                    path: 'datasets/:datasetGroupId/add',
                    lazy: async () => {
                        const { AddDatasetRoute } = await import(
                            './routes/app/datasets/add'
                        );
                        return { Component: AddDatasetRoute };
                    },
                },
                {
                    path: 'tools',
                    lazy: async () => {
                        const { ToolsRoute } = await import(
                            './routes/app/tools/tools'
                        );
                        return { Component: ToolsRoute };
                    },
                },
                {
                    path: 'tools/create',
                    lazy: async () => {
                        const { CreateToolRoute } = await import(
                            './routes/app/tools/create'
                        );
                        return { Component: CreateToolRoute };
                    },
                },
                {
                    path: 'tools/:toolGroupId',
                    lazy: async () => {
                        const { ToolRoute } = await import(
                            './routes/app/tools/tool'
                        );
                        return { Component: ToolRoute };
                    },
                },
                {
                    path: 'graders',
                    lazy: async () => {
                        const { GradersRoute } = await import(
                            './routes/app/graders/graders'
                        );
                        return { Component: GradersRoute };
                    },
                },
                {
                    path: 'graders/:graderGroupId',
                    lazy: async () => {
                        const { GraderRoute } = await import(
                            './routes/app/graders/grader'
                        );
                        return { Component: GraderRoute };
                    },
                },
                {
                    path: 'graders/create',
                    lazy: async () => {
                        const { CreateGraderRoute } = await import(
                            './routes/app/graders/create'
                        );
                        return { Component: CreateGraderRoute };
                    },
                },
                {
                    path: 'insurance-extraction-config',
                    lazy: async () => {
                        const { InsuranceExtractionConfigWorkflowsRoute } =
                            await import(
                                './routes/app/insurance-extraction-config/workflows'
                            );
                        return {
                            Component: InsuranceExtractionConfigWorkflowsRoute,
                        };
                    },
                },
                {
                    path: 'insurance-extraction-config/:workflowId',
                    lazy: async () => {
                        const { InsuranceExtractionConfigWorkflowDetailRoute } =
                            await import(
                                './routes/app/insurance-extraction-config/workflow-detail'
                            );
                        return {
                            Component:
                                InsuranceExtractionConfigWorkflowDetailRoute,
                        };
                    },
                },
                {
                    path: 'insurance-extraction/tasks',
                    lazy: async () => {
                        const { InsuranceExtractionTasksRoute } = await import(
                            './routes/app/insurance-extraction/tasks'
                        );
                        return { Component: InsuranceExtractionTasksRoute };
                    },
                },
                {
                    path: 'insurance-extraction/tasks/new',
                    lazy: async () => {
                        const { InsuranceExtractionTasksNewRoute } =
                            await import(
                                './routes/app/insurance-extraction/tasks-new'
                            );
                        return {
                            Component: InsuranceExtractionTasksNewRoute,
                        };
                    },
                },
                {
                    path: 'insurance-extraction/tasks/:taskId',
                    lazy: async () => {
                        const { InsuranceExtractionTaskDetailRoute } =
                            await import(
                                './routes/app/insurance-extraction/task-detail'
                            );
                        return {
                            Component: InsuranceExtractionTaskDetailRoute,
                        };
                    },
                },
                {
                    path: 'claim-analytics',
                    lazy: async () => {
                        const { ClaimAnalyticsRoute } = await import(
                            './routes/app/claim-analytics'
                        );
                        return { Component: ClaimAnalyticsRoute };
                    },
                },
                {
                    path: 'settings',
                    lazy: async () => {
                        const { SettingsRoute } = await import(
                            './routes/app/settings'
                        );
                        return { Component: SettingsRoute };
                    },
                },
                {
                    path: 'api-keys',
                    lazy: async () => {
                        const { ApiKeysRoute } = await import(
                            './routes/app/api-keys'
                        );
                        return { Component: ApiKeysRoute };
                    },
                },
                {
                    path: 'deployments',
                    lazy: async () => {
                        const { DeploymentsRoute } = await import(
                            './routes/app/deployments/deployments'
                        );
                        return { Component: DeploymentsRoute };
                    },
                },
                {
                    path: 'deployments/:deploymentGroupId',
                    lazy: async () => {
                        const { DeploymentRoute } = await import(
                            './routes/app/deployments/deployment'
                        );
                        return { Component: DeploymentRoute };
                    },
                },
                {
                    path: 'telemetry',
                    lazy: async () => {
                        const { TelemetryRoute } = await import(
                            './routes/app/telemetry/telemetry'
                        );
                        return { Component: TelemetryRoute };
                    },
                },
                {
                    path: 'telemetry/trace-groups',
                    lazy: async () => {
                        const { TraceGroupsRoute } = await import(
                            './routes/app/telemetry/trace-groups'
                        );
                        return { Component: TraceGroupsRoute };
                    },
                },
                {
                    path: 'telemetry/trace-groups/:traceGroupId',
                    lazy: async () => {
                        const { TraceGroupRoute } = await import(
                            './routes/app/telemetry/trace-group'
                        );
                        return { Component: TraceGroupRoute };
                    },
                },
                {
                    path: 'telemetry/trace-groups/:traceGroupId/traces',
                    lazy: async () => {
                        const { TracesRoute } = await import(
                            './routes/app/telemetry/traces'
                        );
                        return { Component: TracesRoute };
                    },
                },
                {
                    path: 'telemetry/trace-groups/:traceGroupId/traces/:traceId',
                    lazy: async () => {
                        const { TraceRoute } = await import(
                            './routes/app/telemetry/trace'
                        );
                        return { Component: TraceRoute };
                    },
                },
                {
                    path: 'ocr',
                    lazy: async () => {
                        const { OCRRoute } = await import(
                            './routes/app/ocr/ocr'
                        );
                        return { Component: OCRRoute };
                    },
                },
                {
                    path: 'entity-extraction',
                    lazy: async () => {
                        const { EntityExtractionRoute } = await import(
                            './routes/app/entity-extraction'
                        );
                        return { Component: EntityExtractionRoute };
                    },
                },
                {
                    path: 'rpm',
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                const { RpmDashboardRoute } = await import(
                                    './routes/app/rpm/index'
                                );
                                return { Component: RpmDashboardRoute };
                            },
                        },
                        {
                            path: 'new',
                            lazy: async () => {
                                const { NewRpmExtractionRoute } = await import(
                                    './routes/app/rpm/new'
                                );
                                return { Component: NewRpmExtractionRoute };
                            },
                        },
                        {
                            path: 'vendor-analytics',
                            lazy: async () => {
                                const { VendorAnalyticsSummaryRoute } =
                                    await import(
                                        './routes/app/rpm/vendor-analytics/index'
                                    );
                                return {
                                    Component: VendorAnalyticsSummaryRoute,
                                };
                            },
                        },
                        {
                            path: 'vendor-analytics/:vendorId',
                            lazy: async () => {
                                const { VendorAnalyticsDetailRoute } =
                                    await import(
                                        './routes/app/rpm/vendor-analytics/$vendorId'
                                    );
                                return {
                                    Component: VendorAnalyticsDetailRoute,
                                };
                            },
                        },
                        {
                            path: 'billing',
                            lazy: async () => {
                                const { RpmBillingRoute } = await import(
                                    './routes/app/rpm/billing'
                                );
                                return { Component: RpmBillingRoute };
                            },
                        },
                        {
                            path: ':id',
                            lazy: async () => {
                                const { PayrollReportDetailRoute } =
                                    await import('./routes/app/rpm/$id');
                                return { Component: PayrollReportDetailRoute };
                            },
                        },
                        {
                            path: 'vendors',
                            lazy: async () => {
                                const { PayrollVendorsRoute } = await import(
                                    './routes/app/rpm/vendors'
                                );
                                return { Component: PayrollVendorsRoute };
                            },
                        },
                        {
                            path: 'rules',
                            lazy: async () => {
                                const { WageExtractionRulesRoute } =
                                    await import('./routes/app/rpm/rules');
                                return { Component: WageExtractionRulesRoute };
                            },
                        },
                    ],
                },
                {
                    path: 'documentation',
                    lazy: async () => {
                        const { DocumentationRoute } = await import(
                            './routes/app/documentation/documentation'
                        );
                        return { Component: DocumentationRoute };
                    },
                },
                {
                    path: 'documentation/introduction',
                    lazy: async () => {
                        const { DocumentationIntroductionRoute } = await import(
                            './routes/app/documentation/introduction'
                        );
                        return { Component: DocumentationIntroductionRoute };
                    },
                },
                {
                    path: 'documentation/api',
                    lazy: async () => {
                        const { ApiDocumentationRoute: DocumentationRoute } =
                            await import(
                                './routes/app/documentation/api-documentation'
                            );
                        return { Component: DocumentationRoute };
                    },
                },
                {
                    path: 'documentation/projects',
                    lazy: async () => {
                        const { ProjectsDocumentation } = await import(
                            './routes/app/documentation/projects'
                        );
                        return { Component: ProjectsDocumentation };
                    },
                },
                {
                    path: 'documentation/datasets',
                    lazy: async () => {
                        const { DatasetsDocumentation } = await import(
                            './routes/app/documentation/datasets'
                        );
                        return { Component: DatasetsDocumentation };
                    },
                },
                {
                    path: 'documentation/secrets',
                    lazy: async () => {
                        const { SecretsDocumentation } = await import(
                            './routes/app/documentation/secrets'
                        );
                        return { Component: SecretsDocumentation };
                    },
                },
                {
                    path: 'documentation/graders',
                    lazy: async () => {
                        const { GradersDocumentation } = await import(
                            './routes/app/documentation/graders'
                        );
                        return { Component: GradersDocumentation };
                    },
                },
                {
                    path: 'documentation/tools',
                    lazy: async () => {
                        const { ToolsDocumentation } = await import(
                            './routes/app/documentation/tools'
                        );
                        return { Component: ToolsDocumentation };
                    },
                },
                {
                    path: 'documentation/implementations',
                    lazy: async () => {
                        const { ImplementationsDocumentation } = await import(
                            './routes/app/documentation/implementations'
                        );
                        return { Component: ImplementationsDocumentation };
                    },
                },
                {
                    path: 'documentation/deployments',
                    lazy: async () => {
                        const { DeploymentsDocumentation } = await import(
                            './routes/app/documentation/deployments'
                        );
                        return { Component: DeploymentsDocumentation };
                    },
                },
                {
                    path: 'documentation/telemetry',
                    lazy: async () => {
                        const { TelemetryDocumentation } = await import(
                            './routes/app/documentation/telemetry'
                        );
                        return { Component: TelemetryDocumentation };
                    },
                },
                {
                    path: 'documentation/ocr',
                    lazy: async () => {
                        const { OcrDocumentation } = await import(
                            './routes/app/documentation/ocr'
                        );
                        return { Component: OcrDocumentation };
                    },
                },
                {
                    path: 'documentation/data-quality-review',
                    lazy: async () => {
                        const { DataQualityReviewDocumentation } = await import(
                            './routes/app/documentation/data-quality-review'
                        );
                        return { Component: DataQualityReviewDocumentation };
                    },
                },
                {
                    path: 'documentation/model-pricing',
                    lazy: async () => {
                        const { ModelPricingRoute } = await import(
                            './routes/app/documentation/model-pricing'
                        );
                        return { Component: ModelPricingRoute };
                    },
                },
                {
                    path: 'secrets',
                    lazy: async () => {
                        const { SecretsRoute } = await import(
                            './routes/app/secrets'
                        );
                        return { Component: SecretsRoute };
                    },
                },
                {
                    path: 'transcription',
                    lazy: async () => {
                        const { TranscriptionRoute } = await import(
                            './routes/app/transcription'
                        );
                        return { Component: TranscriptionRoute };
                    },
                },
                {
                    path: 'fwa-dashboard',
                    lazy: async () => {
                        const { FWADashboardRoute } = await import(
                            './routes/app/fwa-dashboard'
                        );
                        return { Component: FWADashboardRoute };
                    },
                },
                {
                    path: 'evaluations',
                    lazy: async () => {
                        const { EvaluationsRoute } = await import(
                            './routes/app/evaluations'
                        );
                        return { Component: EvaluationsRoute };
                    },
                },
                {
                    path: 'evaluations/:evaluationRunId',
                    lazy: async () => {
                        const { EvaluationDetailRoute } = await import(
                            './routes/app/evaluation-detail'
                        );
                        return { Component: EvaluationDetailRoute };
                    },
                },
                {
                    path: 'pacific-cross-demo',
                    lazy: async () => {
                        const { PacificCrossDemoRoute } = await import(
                            './routes/app/pacific-cross-demo'
                        );
                        return { Component: PacificCrossDemoRoute };
                    },
                },
            ],
        },
        {
            path: '*',
            errorElement: <RouteErrorBoundary />,
            lazy: async () => {
                const { NotFoundRoute } = await import('./routes/not-found');
                return { Component: NotFoundRoute };
            },
        },
    ]);

export const AppRouter = () => {
    const queryClient = useQueryClient();

    const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

    return <RouterProvider router={router} />;
};
